import { Component } from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';

import { BrowserUtils } from '../../../ui-utils/browser-utils';
import style from './toast.component.module.scss';

interface ToastProps {
    /**
     * OPTIONAL since 2026-09-05 (lane N).
     *
     * Every existing caller passes a heading and a body ("Image Modified" / "We made your image
     * bigger…"), and that stays exactly as it was. The PDF editor's messages are single sentences
     * from ONE copy table (`components/pdf-editor/views/notices.copy.ts`) with no heading to give
     * them — inventing a category word here ("Font", "Not applied") would be a second copy source
     * and, per the editor's STATUS §3.4 rule 4, a word on screen that is for us rather than for
     * the reader. A toast with no title renders its sentence alone and skips the heading row.
     */
    title?: string;
    type?: 'primary' | 'success' | 'danger' | 'warning' | 'info';
    message: string;
    timeout?: number;
    canClose?: boolean;
    mustClose?: boolean;
    /**
     * The ARIA role of the toast box (lane N, 2026-09-05).
     *
     * A toast has never carried one, so nothing it said reached a screen reader. The PDF editor's
     * messages used to render as `role="status"` / `role="alert"` rows in the document column and
     * that role WAS their announcement — announced on insertion, which is what a message needs —
     * so moving them here without it would have made every substitution, refusal and page verdict
     * silent. Optional, and unset for every existing caller, so no other toast starts announcing.
     */
    role?: 'status' | 'alert';
    /** The copy row this toast renders, as `data-copy-key` — the handle QA rows address it by. */
    copyKey?: string;
    /** Accessible name for the close control. Unset leaves the control unnamed, as before. */
    closeLabel?: string;
    /**
     * Where to mount this toast instead of the page's `#toasts` (Studio, round 4 G5). The fixed
     * top-right container sits exactly over the Studio header's Export button, so the "Clip
     * removed — Undo" toast swallowed the first Export click for its whole 8 s. An editor that
     * owns its layout hands over a host of its own; every other caller is unchanged.
     */
    host?: HTMLElement | null;
    /**
     * An action the message offers, rendered as one button after the sentence (F15, Studio 6.8).
     *
     * Additive and unset for every existing caller, so no shipped toast grows a button. It exists
     * because four of the Studio's messages are only honest WITH one -- "Removed beach-03.jpg ·
     * Undo", "Video clips muted so your song plays. · Undo" -- and a toast that reports a
     * destructive change with no way back is a notification, not an offer. Pressing it runs
     * `onAction` and then dismisses the toast: a second press would undo the undo, which is not
     * what the word means.
     *
     * The longer timeout 6.8 asks for needs nothing new -- `timeout` has always been a prop and
     * `showToast` merely defaults it to 3000 (:49). The Studio passes 8000.
     */
    actionLabel?: string;
    onAction?: (modalRef: Toast) => void;
    onOpen?: (modalRef: Toast) => void;
    onClose?: (modalRef: Toast) => void;
}

interface ToastState {
    shown: boolean;
}

/** The per-toast container `createToast` mounts into, carrying its React root for `unmount`. */
type ToastContainer = HTMLElement & { __toastRoot?: Root };

export default class Toast extends Component<ToastProps, ToastState> {

    static showToast(request: ToastProps) {
        request.timeout = request.timeout || 3000;
        request.canClose = Object.keys(request).includes('canClose') ? request.canClose : true;
        this.createToast(<Toast {...request}>
        </Toast>, request);
    }

    static createToast(Cmp: any, props: ToastProps) {
        if (!BrowserUtils.hasDocument()) {
            console.error("Requested execution aborted since document was not avalilable");
            return;
        }
        const elementParent = document.createElement('div');
        // Removed bg- classes to rely on component styles
        elementParent.classList.add(`${Date.now()}`);
        let toasts: Element | null = (props.host && props.host.isConnected ? props.host : null) || document.querySelector('#toasts');
        if (!toasts) {
            toasts = document.createElement('div');
            toasts.id = 'toasts';
            document.body.appendChild(toasts);
        }
        if (toasts) {
            toasts.appendChild(elementParent);
            const root = createRoot(elementParent!);
            // Kept on the container so `unmount()` can end the root, not just remove the element
            // (Studio round 4, memory audit: see `unmount`).
            (elementParent as ToastContainer).__toastRoot = root;
            root.render(Cmp);
        } else {
            console.error('Toasts not found');
        }
    }

    readonly state: Readonly<ToastState> = {
        shown: true,
    };
    constructor(props: ToastProps) {
        super(props);
        if (!props.mustClose && props.canClose) {
            this.timer = setTimeout(() => {
                this.unmount();
            }, props.timeout);
        }
    }
    parentElement;
    /**
     * The auto-dismiss timer, and the latch that makes `unmount()` happen once (2026-09-13).
     *
     * The timer was armed and never cleared, and `unmount()` had no guard -- so a toast that was
     * dismissed by hand still dismissed itself again when its timeout elapsed: `onClose` twice
     * and a `setState` against a detached node. That was survivable while the only control was
     * the close button; it is the NORMAL path for an action toast, whose whole point is that the
     * button gets pressed (F15).
     */
    private timer: ReturnType<typeof setTimeout> | undefined;
    private closed = false;
    setParentElement(element) {
        this.parentElement = element && element.parentElement;
    }

    componentDidMount() {
        if (this.props.onOpen) {
            this.props.onOpen(this);
        }
    }

    public unmount() {
        if (this.closed) return;
        this.closed = true;
        if (this.timer !== undefined) {
            clearTimeout(this.timer);
            this.timer = undefined;
        }
        this.setState({
            shown: false,
        });
        if (this.props.onClose) {
            this.props.onClose(this);
        }
        try {
            if (this.parentElement) {
                this.parentElement.remove();
            }
        } catch (e) {
            console.error('Error while removing modal');
        }
        // The React root `createToast` made was never unmounted, only its element removed -- so
        // every toast left its root alive for the life of the page: the detached container with
        // React's ~140 delegated listeners on it and the toast's own subtree (Studio round 4
        // memory audit: +700 JS event listeners and +130 DOM nodes per six "Clip removed" toasts,
        // never reclaimed by GC). Deferred a tick, because this runs from the root's own event
        // handler or timer and a root may not be unmounted while it renders.
        const container = this.parentElement as ToastContainer | null;
        const root = container?.__toastRoot;
        if (root) {
            container.__toastRoot = undefined;
            setTimeout(() => root.unmount(), 0);
        }
    }

    render() {
        return (
            <div
                ref={(r) => this.setParentElement(r)}
                className={style.toast}
                data-type={this.props.type || 'primary'}
                data-copy-key={this.props.copyKey}
                role={this.props.role}
            >
                <div className={style.content}>
                    {/* The heading row is what the close control is positioned against, so it
                        renders whenever there is EITHER a title or a close button — a titleless
                        toast keeps its ×, it just has no words above the sentence. */}
                    {(this.props.title || this.props.canClose) &&
                        <div className={style.head}>
                            <div className={style['header-content']}>{this.props.title}</div>
                            {
                                this.props.canClose &&
                                // A BUTTON, not a div (lane N, 2026-09-05): the only way to
                                // dismiss a toast was a mouse press on an unnamed div, so a
                                // keyboard user could not reach it and a screen reader could not
                                // name it. The class and the geometry are untouched.
                                <button type="button" className={style.close}
                                    aria-label={this.props.closeLabel}
                                    style={{ border: 0, padding: 0, background: 'transparent' }}
                                    onClick={() => this.unmount()}>
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M9 1L1 9M1 1L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            }
                        </div>
                    }
                    <div className={style.body}>{this.props.message}</div>
                    {this.props.actionLabel && this.props.onAction &&
                        <div className={style.actions}>
                            <button
                                type="button"
                                className={style.action}
                                data-testid="toast-action"
                                onClick={() => {
                                    // The caller's work first, then the dismissal: `unmount` runs
                                    // `onClose` and detaches the node, and an action that read
                                    // anything off this component afterwards would be reading a
                                    // dead one.
                                    this.props.onAction!(this);
                                    this.unmount();
                                }}
                            >
                                {this.props.actionLabel}
                            </button>
                        </div>
                    }
                </div>
            </div>
        );
    }
}